import { useState } from 'react';
import type { HotStreakState, HotStreakPrivateState } from '@flip7/shared';
import { HS_CARD_SUBMIT_TIMER_MS, HS_MASCOTS } from '@flip7/shared';
import { useCountdown } from './useCountdown';
import { useHotStreakTips } from '../../hooks/useHotStreakTips';
import { Tooltip } from './Tooltip';

interface Props {
  state: HotStreakState;
  playerId: string | null;
  privateState: HotStreakPrivateState | null;
  onSubmitCard: (cardId: string) => void;
}

export function HotStreakCardSubmit({ state, playerId, privateState, onSubmitCard }: Props) {
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const tips = useHotStreakTips();
  const timeLeft = useCountdown(state.submissionTimerStartedAt, HS_CARD_SUBMIT_TIMER_MS);
  const player = state.players.find((p) => p.id === playerId);
  const hasSubmitted = player?.submittedCard ?? false;
  const playersSubmitted = state.players.filter((p) => p.submittedCard).length;

  const handleSubmit = () => {
    if (selectedCardId) {
      onSubmitCard(selectedCardId);
      setSelectedCardId(null);
    }
  };

  return (
    <div className="hs-card-submit">
      <h2>Secret Card</h2>
      <p className="hs-subtitle">
        Choose a card to sneak into the racing deck
      </p>
      <div className="hs-timer">{Math.ceil(timeLeft / 1000)}s</div>

      {hasSubmitted ? (
        <div className="hs-waiting">
          <p>Card submitted! Waiting for others...</p>
          <p className="hs-submit-progress">
            {playersSubmitted}/{state.players.length} submitted
          </p>
        </div>
      ) : (
        <>
          <div className="hs-hand">
            {privateState?.hand.map((card) => {
              const mascotInfo = card.mascot !== 'all' ? HS_MASCOTS[card.mascot] : null;
              return (
                <button
                  key={card.id}
                  className={`hs-hand-card ${selectedCardId === card.id ? 'selected' : ''}`}
                  style={{ borderColor: mascotInfo?.color ?? '#4a9' }}
                  onClick={() => setSelectedCardId(card.id)}
                >
                  <div className="hs-card-mascot" style={{ color: mascotInfo?.color ?? '#4a9' }}>
                    {mascotInfo?.name ?? 'ALL'}
                  </div>
                  <div className="hs-card-type">
                    {card.type === 'number' && `${(card.value ?? 0) > 0 ? '+' : ''}${card.value}`}
                    {card.type === 'star' && 'Star'}
                    {card.type === 'turn-around' && 'Turn Around'}
                    {card.type === 'fall-down' && 'Fall Down'}
                    {card.type === 'swerve-left' && 'Swerve Left'}
                    {card.type === 'swerve-right' && 'Swerve Right'}
                    {card.type === 'recover' && 'Recover'}
                    {card.type === 'multi' && `All: ${card.multiEffect?.type}`}
                  </div>
                </button>
              );
            })}
          </div>
          <button
            className="btn btn-primary hs-submit-btn"
            disabled={!selectedCardId}
            onClick={handleSubmit}
          >
            Submit Card
          </button>
        </>
      )}

      {!hasSubmitted && tips.shouldShow('card-intro') && (
        <Tooltip
          icon="🃏"
          message="Choose a card to secretly add to the race deck. This is your chance to influence which mascots move — and how!"
          onDismiss={() => tips.dismiss('card-intro')}
        />
      )}
    </div>
  );
}
